<template>
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

    <div v-if="loading" class="text-center">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
      <p class="mt-2 text-gray-600">Loading...</p>
    </div>

    <div v-else-if="error" class="text-center">
      <p class="text-red-600">{{ error }}</p>
      <button 
        @click="initializeList" 
        class="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
      >
        Try Again
      </button>
    </div>

    <div v-else-if="currentPackingList" class="space-y-6">
      <!-- Header -->
      <div class="bg-white rounded-lg shadow-sm border p-6">        
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center space-x-4">
            <router-link 
              to="/overview" 
              class="text-blue-600 hover:text-blue-700 transition-colors text-sm font-medium flex items-center"
              title="View all lists"
            >
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
              All Lists
            </router-link>
            <h1 class="text-2xl font-bold text-gray-900" @click="editTitle">
              {{ currentPackingList?.title || 'Loading...' }}
            </h1>
          </div>
          <div class="flex items-center space-x-2">
            <button 
              @click="exportList"
              class="text-blue-600 hover:text-blue-700 transition-colors"
              title="Export as YAML"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
            <button 
              @click="openImportDialog"
              class="text-purple-600 hover:text-purple-700 transition-colors"
              title="Import from YAML"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </button>
            <button 
              @click="generatePrintVersion"
              class="text-orange-600 hover:text-orange-700 transition-colors"
              title="Generate printable text version"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
            </button>
            <button 
              @click="saveAsNewList"
              class="text-green-600 hover:text-green-700 transition-colors"
              title="Save as new list"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
            <button 
              @click="shareList"
              class="text-gray-500 hover:text-gray-700 transition-colors"
              title="Share this list"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
            </button>
          </div>
        </div>
        <p v-if="currentPackingList.description" class="text-gray-600 mb-4" @click="editDescription">
          {{ currentPackingList.description }}
        </p>
        
        <!-- Progress -->
        <div class="mt-4">
          <div class="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>{{ completedItems }} of {{ totalItems }} items completed</span>
            <span>{{ progress }}%</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div 
              class="bg-green-600 h-2 rounded-full transition-all duration-300"
              :style="{ width: `${progress}%` }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Categories -->
      <draggable
        v-model="localCategories"
        group="categories"
        item-key="id"
        handle=".category-drag-handle"
        class="space-y-4"
        @end="onCategoryEnd"
        ghost-class="opacity-50"
        chosen-class="ring-2"
        animation="150"
      >
        <template #item="{ element }">
          <div class="relative group">
            <div class="category-drag-handle absolute -left-8 top-4 cursor-move text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
              </svg>
            </div>
            <CategoryComponent
              :key="element.id"
              :category="element"
              @update-category="updateCategory"
              @delete-category="deleteCategory"
              @create-item="createItem"
              @update-item="updateItem"
              @delete-item="deleteItem"
              @reorder-items="handleReorderItems"
              @move-item="handleMoveItem"
            />
          </div>
        </template>
      </draggable>

      <!-- Add Category Button -->
      <button 
        @click="openAddCategoryDialog"
        class="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-4 px-6 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors"
      >
        + Add Category
      </button>
    </div>

    <!-- Add Category Dialog -->
    <dialog 
      ref="addCategoryDialog" 
      class="backdrop:bg-black backdrop:bg-opacity-50 p-0 rounded-lg shadow-xl max-w-md w-full border-0"
      @click="handleDialogBackdropClick"
    >
      <div class="p-6 bg-white rounded-lg">
        <h3 class="text-lg font-semibold mb-4">Add New Category</h3>
        <form @submit.prevent="handleCreateCategory">
          <input
            ref="categoryNameInput"
            v-model="newCategoryName"
            type="text"
            placeholder="Category name"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            required
            @keydown.escape="closeAddCategoryDialog"
          >
          <div class="flex justify-end space-x-2">
            <button 
              type="button"
              @click="closeAddCategoryDialog"
              class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Add Category
            </button>
          </div>
        </form>
      </div>
    </dialog>

    <!-- Edit Title Dialog -->
    <dialog 
      ref="editTitleDialog" 
      class="backdrop:bg-black backdrop:bg-opacity-50 p-0 rounded-lg shadow-xl max-w-md w-full border-0"
      @click="handleDialogBackdropClick"
    >
      <div class="p-6 bg-white rounded-lg">
        <h3 class="text-lg font-semibold mb-4">Edit Title</h3>
        <form @submit.prevent="handleUpdateTitle">
          <input
            ref="titleInput"
            v-model="editTitleValue"
            type="text"
            placeholder="List title"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            required
            @keydown.escape="closeEditTitleDialog"
          >
          <div class="flex justify-end space-x-2">
            <button 
              type="button"
              @click="closeEditTitleDialog"
              class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </dialog>

    <!-- Save as New List Dialog -->
    <dialog 
      ref="saveAsNewDialog" 
      class="backdrop:bg-black backdrop:bg-opacity-50 p-0 rounded-lg shadow-xl max-w-md w-full border-0"
      @click="handleDialogBackdropClick"
    >
      <div class="p-6 bg-white rounded-lg">
        <h3 class="text-lg font-semibold mb-4">Save as New List</h3>
        <form @submit.prevent="handleSaveAsNew">
          <input
            ref="newListNameInput"
            v-model="newListName"
            type="text"
            placeholder="New list name"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            required
            @keydown.escape="closeSaveAsNewDialog"
          >
          <div class="flex justify-end space-x-2">
            <button 
              type="button"
              @click="closeSaveAsNewDialog"
              class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              :disabled="loading"
            >
              {{ loading ? 'Creating...' : 'Create New List' }}
            </button>
          </div>
        </form>
      </div>
    </dialog>

    <!-- Import YAML Dialog -->
    <dialog 
      ref="importDialog" 
      class="backdrop:bg-black backdrop:bg-opacity-50 p-0 rounded-lg shadow-xl max-w-lg w-full border-0"
      @click="handleDialogBackdropClick"
    >
      <div class="p-6 bg-white rounded-lg">
        <h3 class="text-lg font-semibold mb-4">Import from YAML</h3>
        <form @submit.prevent="handleImport">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Upload YAML file or paste content:
            </label>
            <input
              ref="fileInput"
              type="file"
              accept=".yaml,.yml"
              @change="handleFileSelect"
              class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 mb-4"
            >
            <textarea
              ref="yamlInput"
              v-model="yamlContent"
              placeholder="Or paste YAML content here..."
              rows="8"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
              @keydown.escape="closeImportDialog"
            ></textarea>
          </div>
          <div class="flex justify-end space-x-2">
            <button 
              type="button"
              @click="closeImportDialog"
              class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              class="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              :disabled="!yamlContent.trim() || loading"
            >
              {{ loading ? 'Importing...' : 'Import List' }}
            </button>
          </div>
        </form>
      </div>
    </dialog>

    <!-- Print Version Dialog -->
    <dialog 
      ref="printDialog" 
      class="backdrop:bg-black backdrop:bg-opacity-50 p-0 rounded-lg shadow-xl max-w-2xl w-full border-0"
      @click="handleDialogBackdropClick"
    >
      <div class="p-6 bg-white rounded-lg">
        <h3 class="text-lg font-semibold mb-4">Printable Text Version</h3>
        <div class="mb-4">
          <textarea
            ref="printTextArea"
            v-model="printableText"
            readonly
            rows="20"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-vertical font-mono text-sm bg-gray-50"
          ></textarea>
        </div>
        <div class="flex justify-between items-center">
          <div class="flex items-center space-x-2">
            <label class="flex items-center space-x-2">
              <input 
                type="checkbox" 
                v-model="includeCheckboxes"
                @change="updatePrintableText"
                class="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              >
              <span class="text-sm text-gray-700">Include checkboxes</span>
            </label>
            <label class="flex items-center space-x-2">
              <input 
                type="checkbox" 
                v-model="includeQuantities"
                @change="updatePrintableText"
                class="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              >
              <span class="text-sm text-gray-700">Include quantities</span>
            </label>
          </div>
          <div class="flex space-x-2">
            <button 
              @click="copyToClipboard"
              class="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Copy to Clipboard
            </button>
            <button 
              @click="downloadTextFile"
              class="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
            >
              Download as .txt
            </button>
            <button 
              @click="closePrintDialog"
              class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePackingListStore } from '@/stores/packingList'
import CategoryComponent from '@/components/CategoryComponent.vue'
import draggable from 'vuedraggable'
import * as yaml from 'js-yaml'
import type { Category, Item } from '@/types'

const props = defineProps<{
  id: string
}>()

const route = useRoute()
const router = useRouter()
const store = usePackingListStore()

// Dialog refs
const addCategoryDialog = ref<HTMLDialogElement>()
const editTitleDialog = ref<HTMLDialogElement>()
const saveAsNewDialog = ref<HTMLDialogElement>()
const importDialog = ref<HTMLDialogElement>()
const printDialog = ref<HTMLDialogElement>()
const categoryNameInput = ref<HTMLInputElement>()
const titleInput = ref<HTMLInputElement>()
const newListNameInput = ref<HTMLInputElement>()
const yamlInput = ref<HTMLTextAreaElement>()
const fileInput = ref<HTMLInputElement>()
const printTextArea = ref<HTMLTextAreaElement>()

// Form data
const newCategoryName = ref('')
const editTitleValue = ref('')
const newListName = ref('')
const yamlContent = ref('')
const printableText = ref('')
const includeCheckboxes = ref(true)
const includeQuantities = ref(true)

// Track the actual list ID (may differ from props.id when creating new)
const actualListId = ref(props.id)

// Computed
const loading = computed(() => store.loading)
const error = computed(() => store.error)
const currentPackingList = computed(() => store.currentPackingList)
const categories = computed(() => store.categories)
const totalItems = computed(() => store.totalItems)
const completedItems = computed(() => store.completedItems)
const progress = computed(() => store.progress)

const sortedCategories = computed(() => categories.value.sort((a, b) => a.order - b.order))
const localCategories = ref<(Category & { items: Item[] })[]>([])

// Keep localCategories in sync with store categories
watch(sortedCategories, (newCategories) => {
  localCategories.value = [...newCategories]
}, { deep: true, immediate: true })

// Methods
const initializeList = async () => {
  if (props.id === 'new') {
    // Create new list
    const title = 'My Packing List'
    try {
      const newId = await store.createPackingList(title)
      if (newId) {
        // Update the actual ID we're working with
        actualListId.value = newId
        // Update URL in browser address bar without navigation
        router.replace({ name: 'packing-list', params: { id: newId } })
      }
    } catch (err) {
      console.error('Failed to create new list:', err)
    }
  } else {
    // Load existing list
    actualListId.value = props.id
    await store.loadPackingList(props.id)
  }
}

const shareList = () => {
  const url = window.location.href
  navigator.clipboard.writeText(url).then(() => {
    alert('Link copied to clipboard!')
  }).catch(() => {
    // Fallback - show the URL
    alert(`Share this link: ${url}`)
  })
}

const editTitle = () => {
  if (currentPackingList.value) {
    editTitleValue.value = currentPackingList.value.title
    editTitleDialog.value?.showModal()
    // Focus input after modal opens
    setTimeout(() => {
      titleInput.value?.focus()
      titleInput.value?.select()
    }, 100)
  }
}

const editDescription = () => {
  // TODO: Implement description editing
}

const openAddCategoryDialog = () => {
  addCategoryDialog.value?.showModal()
  setTimeout(() => {
    categoryNameInput.value?.focus()
  }, 100)
}

const handleCreateCategory = async () => {
  if (newCategoryName.value.trim()) {
    await store.createCategory(newCategoryName.value.trim())
    newCategoryName.value = ''
    closeAddCategoryDialog()
  }
}

const handleUpdateTitle = async () => {
  if (editTitleValue.value.trim()) {
    await store.updatePackingList({ title: editTitleValue.value.trim() })
    closeEditTitleDialog()
  }
}

const closeAddCategoryDialog = () => {
  addCategoryDialog.value?.close()
  newCategoryName.value = ''
}

const closeEditTitleDialog = () => {
  editTitleDialog.value?.close()
  editTitleValue.value = ''
}

const saveAsNewList = () => {
  if (currentPackingList.value) {
    newListName.value = `${currentPackingList.value.title} (Copy)`
    saveAsNewDialog.value?.showModal()
    setTimeout(() => {
      newListNameInput.value?.focus()
      newListNameInput.value?.select()
    }, 100)
  }
}

const handleSaveAsNew = async () => {
  if (newListName.value.trim() && currentPackingList.value) {
    try {
      const newId = await store.duplicatePackingList(currentPackingList.value.id, newListName.value.trim())
      if (newId) {
        closeSaveAsNewDialog()
        // Navigate to the new list (watcher will handle reload)
        await router.push({ name: 'packing-list', params: { id: newId } })
      }
    } catch (err) {
      console.error('Failed to duplicate list:', err)
    }
  }
}

const closeSaveAsNewDialog = () => {
  saveAsNewDialog.value?.close()
  newListName.value = ''
}

// Export/Import functions
const exportList = () => {
  if (!currentPackingList.value) return
  
  // Create YAML structure
  const yamlData = {
    title: currentPackingList.value.title,
    description: currentPackingList.value.description || '',
    categories: categories.value.map(category => ({
      name: category.name,
      items: category.items.map(item => ({
        name: item.name,
        quantity: item.quantity || 1,
        notes: item.notes || '',
        completed: item.completed || false
      }))
    }))
  }
  
  // Convert to YAML string
  const yamlString = yaml.dump(yamlData, {
    indent: 2,
    lineWidth: -1,
    quotingType: '"'
  })
  
  // Create and download file
  const blob = new Blob([yamlString], { type: 'text/yaml' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${currentPackingList.value.title.replace(/[^a-z0-9]/gi, '_')}.yaml`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const openImportDialog = () => {
  yamlContent.value = ''
  if (fileInput.value) fileInput.value.value = ''
  importDialog.value?.showModal()
  setTimeout(() => {
    yamlInput.value?.focus()
  }, 100)
}

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      yamlContent.value = e.target?.result as string
    }
    reader.readAsText(file)
  }
}

const handleImport = async () => {
  if (!yamlContent.value.trim()) return
  
  try {
    // Parse YAML
    const data = yaml.load(yamlContent.value) as any
    
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid YAML format')
    }
    
    if (!data.title || !Array.isArray(data.categories)) {
      throw new Error('YAML must contain title and categories')
    }
    
    // Import as new list
    const newId = await store.importFromYAML(data)
    if (newId) {
      closeImportDialog()
      // Navigate to the imported list
      await router.push(`/list/${newId}`)
    }
  } catch (err) {
    console.error('Failed to import YAML:', err)
    alert(`Import failed: ${err instanceof Error ? err.message : 'Invalid YAML format'}`)
  }
}

const closeImportDialog = () => {
  importDialog.value?.close()
  yamlContent.value = ''
  if (fileInput.value) fileInput.value.value = ''
}

// Print version functions
const generatePrintVersion = () => {
  if (!currentPackingList.value) return
  
  updatePrintableText()
  printDialog.value?.showModal()
}

const updatePrintableText = () => {
  if (!currentPackingList.value) return
  
  let text = ''
  
  // Header
  text += `${currentPackingList.value.title}\n`
  text += '='.repeat(currentPackingList.value.title.length) + '\n\n'
  
  if (currentPackingList.value.description) {
    text += `${currentPackingList.value.description}\n\n`
  }
  
  // Progress summary
  text += `Progress: ${completedItems.value}/${totalItems.value} items (${progress.value}%)\n\n`
  
  // Categories and items
  categories.value.forEach((category, categoryIndex) => {
    text += `${categoryIndex + 1}. ${category.name.toUpperCase()}\n`
    text += '-'.repeat(category.name.length + 4) + '\n'
    
    if (category.items.length === 0) {
      text += '   (No items)\n\n'
    } else {
      category.items.forEach((item, itemIndex) => {
        let line = `   ${itemIndex + 1}. `
        
        // Add checkbox if enabled
        if (includeCheckboxes.value) {
          line += `[${item.completed ? 'x' : ' '}] `
        }
        
        // Add item name
        line += item.name
        
        // Add quantity if enabled and > 1
        if (includeQuantities.value && item.quantity && item.quantity > 1) {
          line += ` (${item.quantity}x)`
        }
        
        // Add notes if available
        if (item.notes) {
          line += ` - ${item.notes}`
        }
        
        text += line + '\n'
      })
      text += '\n'
    }
  })
  
  // Footer
  text += `\nGenerated: ${new Date().toLocaleString()}\n`
  text += `Total Categories: ${categories.value.length}\n`
  text += `Total Items: ${totalItems.value}\n`
  
  printableText.value = text
}

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(printableText.value)
    alert('Copied to clipboard!')
  } catch (err) {
    console.error('Failed to copy:', err)
    // Fallback: select text
    printTextArea.value?.select()
    alert('Please manually copy the selected text')
  }
}

const downloadTextFile = () => {
  if (!currentPackingList.value) return
  
  const blob = new Blob([printableText.value], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${currentPackingList.value.title.replace(/[^a-z0-9]/gi, '_')}.txt`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const closePrintDialog = () => {
  printDialog.value?.close()
  printableText.value = ''
}

// Handle dialog backdrop clicks (close on click outside)
const handleDialogBackdropClick = (event: MouseEvent) => {
  const dialog = event.target as HTMLDialogElement
  if (event.target === dialog && dialog && typeof dialog.close === 'function') {
    dialog.close()
    // Clean up form data
    if (dialog === addCategoryDialog.value) {
      newCategoryName.value = ''
    } else if (dialog === editTitleDialog.value) {
      editTitleValue.value = ''
    } else if (dialog === saveAsNewDialog.value) {
      newListName.value = ''
    } else if (dialog === importDialog.value) {
      yamlContent.value = ''
      if (fileInput.value) fileInput.value.value = ''
    } else if (dialog === printDialog.value) {
      printableText.value = ''
    }
  }
}

// Store method proxies
const updateCategory = store.updateCategory
const deleteCategory = store.deleteCategory
const createItem = store.createItem
const updateItem = store.updateItem
const deleteItem = store.deleteItem

// Drag and drop handlers
const onCategoryEnd = (event: any) => {
  console.log('Category drag end:', event)
  const { oldIndex, newIndex } = event
  
  if (oldIndex !== newIndex) {
    // Get the new order from localCategories (which was updated by vuedraggable)
    const newOrder = localCategories.value.map(category => category.id)
    
    console.log('Category new order:', newOrder)
    if (newOrder.length > 0) {
      store.reorderCategories(newOrder)
    }
  }
}

const handleReorderItems = (categoryId: string, newOrder: string[]) => {
  console.log('PackingList: handleReorderItems called with:', categoryId, newOrder)
  store.reorderItems(categoryId, newOrder)
}

const handleMoveItem = (itemId: string, newCategoryId: string, newOrder: number) => {
  console.log('PackingList: handleMoveItem called with:', itemId, newCategoryId, newOrder)
  store.moveItemToCategory(itemId, newCategoryId, newOrder)
}


// Watch for route changes
watch(() => props.id, (newId, oldId) => {
  if (newId !== oldId && newId !== 'new') {
    actualListId.value = newId
    store.loadPackingList(newId)
  }
}, { immediate: false })

// Lifecycle
onMounted(() => {
  initializeList()
})
</script>